// GeexTonalBars — Geex frame 06 (workspace/design-refs/geex/
// 06-analytics-tonal-bars.png, "Income & Expenses"): a flat bar chart with
// NO axis/gridlines — bars alternate a flat ink-alpha (or hue-tonal, see
// `hue`) fill with a HATCHED texture variant (repeating-linear-gradient on
// the bar FILL only — the ticket's sanctioned data-texture device), a
// period rail in ink.45 above, and the value rendered directly above each
// bar.
//
// items: [{ label, value, hatched?, title? }] — value drives bar height
// (proportional to the series max, min-floored so a zero value still
// reads as a sliver, never nothing). hatched defaults to alternating by
// index (even = solid, odd = hatched) when not explicitly set per item.
// title: optional — the hover text of that period's label and bar (e.g. the
// full date and the exact total); none, no title attribute.
// ink: base foreground token (default 'title'). hue: optional — poster-
// tonal steps (`hue+10`/`hue-10`) instead of ink-alpha when set.
// height: plot-band height — a spacing-scale token (chart geometry rides
// the same em-based sequence as everything else; default 'E1', ~144px at
// base scale).

// FA204 / frank trap — helper lives in `scope`, not module scope (a bare
// module-scope const reads as undefined post-frank.toJSON and kills the
// Plot children factory; the Rail rendered while the bars silently didn't).
export const GeexTonalBars = {
  scope: {
    barTone: (ink, hue, i) =>
      hue ? (i % 2 ? `${hue}-10` : `${hue}+10`) : i % 2 ? `${ink}.15` : `${ink}.35`
  },
  flow: 'y',
  gap: 'Y',
  width: '100%',
  items: [],
  ink: 'title',
  height: 'E1',

  Rail: {
    display: 'grid',
    gridTemplateColumns: (el) => `repeat(${(el.parent.items || []).length || 1}, 1fr)`,
    gap: 'X',
    childrenAs: 'state',
    children: (el, s) =>
      (el.parent.items || []).map((it, i) => ({
        key: it.label || `p${i}`,
        label: it.label || '',
        title: it.title || ''
      })),
    childExtends: {
      tag: 'span',
      fontSize: 'Z',
      textAlign: 'center',
      // The QUIET INK, never an opacity: ink at 0.45 read 4.14:1 dark and
      // 3.27:1 light (admin census, 2026-09-24). `inactive` in light and
      // `caption` in dark clear 4.5 on the sheet, a card and the stage.
      color: 'inactive',
      '@dark': { color: 'caption' },
      title: (el, s) => s.title || null,
      // `items[].label` is a KEY-IN-STATE carrier: a consumer may hand this
      // chart a bundle key (workspace/pages/admin/usage.js feeds
      // `admin.usage.attr.systemEmpty`). Resolved HERE, at the render site —
      // `translate()` returns its input unchanged when nothing resolves, so a
      // tenant-supplied label passes through untouched.
      text: (el, s) => el.call('polyglot', s.label || '')
    }
  },

  Plot: {
    display: 'grid',
    gridTemplateColumns: (el) => `repeat(${(el.parent.items || []).length || 1}, 1fr)`,
    alignItems: 'end',
    gap: 'X',
    height: (el) => el.parent.height || 'E1',
    childrenAs: 'state',
    children: (el, s) => {
      const parent = el.parent
      const items = parent.items || []
      const max = items.reduce((n, it) => Math.max(n, Number(it.value) || 0), 0) || 1
      const ink = parent.ink || 'title'
      const hue = parent.hue
      return items.map((it, i) => ({
        key: it.label || `bar${i}`,
        pct: Math.max((Number(it.value) || 0) / max, 0.02) * 100,
        hatched: typeof it.hatched === 'boolean' ? it.hatched : i % 2 === 1,
        tone: el.scope.barTone(ink, hue, i),
        value: it.value,
        ink,
        title: it.title || ''
      }))
    },
    childExtends: {
      flow: 'y',
      justifyContent: 'flex-end',
      alignItems: 'center',
      height: '100%',
      gap: 'X',
      title: (el, s) => s.title || null,

      Value: {
        tag: 'span',
        fontSize: 'Z',
        fontFamily: 'Code',
        fontWeight: '700',
        color: (el, s) => s.ink,
        text: (el, s) => String(s.value ?? '')
      },

      Fill: {
        width: '100%',
        height: (el, s) => `${s.pct}%`,
        transition: 'A defaultBezier height',
        color: (el, s) => s.tone,
        background: (el, s) => (s.hatched ? 'transparent' : 'currentColor'),
        backgroundImage: (el, s) =>
          s.hatched
            ? // the stripe and its period on the spacing scale (X ≈ 3.4 px, Y1 ≈ 6.7 px
              // at 14 px), not raw pixels — they follow the chart's own size
              'repeating-linear-gradient(135deg, currentColor 0, currentColor var(--spacing-X), transparent var(--spacing-X), transparent var(--spacing-Y1))'
            : 'none'
      }
    }
  }
}
